'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import type { VehicleOption } from './TaxiBookingClient';

interface FareData {
  baseFare: number;
  kmCharge: number;
  km: number;
  driverAllowance: number;
  nightCharges: number;
  hillCharges: number;
  stateTax: number;
  permit: number;
  tollTax: number;
  parking: number;
  subtotal: number;
  gst: number;
  returnCharges: number;
  total: number;
}

interface FareBreakupProps {
  fare: FareData;
  vehicle: VehicleOption;
  tripType: string;
  estimatedKm: number;
  couponDiscount: number;
  finalTotal: number;
}

export default function FareBreakup({
  fare,
  vehicle,
  tripType,
  estimatedKm,
  couponDiscount,
  finalTotal,
}: FareBreakupProps) {
  const [expanded, setExpanded] = useState(true);

  const rows = [
    {
      id: 'row-base',
      label: 'Base Fare',
      value: fare.baseFare,
      tooltip: `Minimum ${vehicle.minKm} km base charge`,
    },
    {
      id: 'row-km',
      label: `Distance Charge (${fare.km} km × ₹${vehicle.perKm})`,
      value: fare.kmCharge,
      tooltip: 'Calculated from pickup to drop',
    },
    {
      id: 'row-driver',
      label: 'Driver Allowance',
      value: fare.driverAllowance,
      tooltip: 'Daily driver stay and meal allowance',
    },
    {
      id: 'row-night',
      label: 'Night Charges',
      value: fare.nightCharges,
      tooltip: 'Applies for return trip night stay',
      highlight: fare.nightCharges > 0,
    },
    {
      id: 'row-hill',
      label: 'Hill Charges',
      value: fare.hillCharges,
      tooltip: 'Extra charges for mountain terrain routes',
    },
    {
      id: 'row-state',
      label: 'State Tax',
      value: fare.stateTax,
      tooltip: 'Himachal Pradesh state entry tax',
    },
    {
      id: 'row-permit',
      label: 'Permit Charges',
      value: fare.permit,
      tooltip: 'Inter-state permit for commercial vehicle',
    },
    {
      id: 'row-toll',
      label: 'Toll Tax (Estimated)',
      value: fare.tollTax,
      tooltip: 'National highway toll charges — may vary',
    },
    {
      id: 'row-parking',
      label: 'Parking Charges',
      value: fare.parking,
      tooltip: 'Destination parking fees',
    },
    {
      id: 'row-gst',
      label: 'GST (5%)',
      value: fare.gst,
      tooltip: 'Goods and Services Tax on taxi services',
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-5 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <h3 className="font-bold text-foreground text-base">Fare Breakup</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {vehicle.name} · {tripType === 'round-trip' ? 'Round Trip' : 'One Way'} · ~{estimatedKm} km
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-primary font-extrabold text-lg font-tabular">
            ₹{finalTotal.toLocaleString('en-IN')}
          </span>
          {expanded ? (
            <ChevronUp size={18} className="text-muted-foreground" />
          ) : (
            <ChevronDown size={18} className="text-muted-foreground" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-border">
          <div className="pt-3">
            {rows.map((row) => (
              <div key={row.id} className="fare-row">
                <div className="flex items-center gap-1.5">
                  <span className={row.highlight ? 'text-amber-600 font-medium' : 'text-muted-foreground'}>
                    {row.label}
                  </span>
                  <div className="relative group/tooltip">
                    <Info size={12} className="text-muted-foreground/50 cursor-help" />
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-48 bg-foreground text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-150 pointer-events-none z-10 shadow-lg">
                      {row.tooltip}
                    </div>
                  </div>
                </div>
                <span className={`font-semibold font-tabular ${row.value === 0 ? 'text-muted-foreground' : 'text-foreground'}`}>
                  {row.value === 0 ? '—' : `₹${row.value.toLocaleString('en-IN')}`}
                </span>
              </div>
            ))}

            {tripType === 'round-trip' && fare.returnCharges > 0 && (
              <div className="fare-row">
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Return Trip Charges (85%)</span>
                </div>
                <span className="font-semibold font-tabular">
                  ₹{fare.returnCharges.toLocaleString('en-IN')}
                </span>
              </div>
            )}

            {couponDiscount > 0 && (
              <div className="fare-row">
                <span className="text-secondary font-semibold">Coupon Discount</span>
                <span className="text-secondary font-bold font-tabular">
                  −₹{couponDiscount.toLocaleString('en-IN')}
                </span>
              </div>
            )}

            <div className="fare-row total mt-2">
              <span>Total Payable</span>
              <span>₹{finalTotal.toLocaleString('en-IN')}</span>
            </div>
          </div>

          <div className="mt-4 bg-secondary-light rounded-xl p-3 flex items-start gap-2">
            <Info size={14} className="text-secondary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-secondary leading-relaxed">
              This is an estimated fare. Final fare may vary based on actual distance, waiting
              time, and route conditions. GST invoice will be provided after trip completion.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}